import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import Pagination from "../components/Pagination";
import { fetchLeaderboard } from "../lib/api";

/**
 * Per-TCG leaderboard, ranked by total_upvotes on approved
 * submissions. V1 is Pokémon-only; tcg_id is fixed here but the API
 * accepts other slugs.
 */
export default function LeaderboardPage() {
  const [state, setState] = useState("loading");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setState("loading");
    fetchLeaderboard({ tcgId: "pokemon", page })
      .then((d) => { if (alive) { setData(d); setState("ready"); } })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || e?.message || "Failed to load");
        setState("error");
      });
    return () => { alive = false; };
  }, [page]);

  return (
    <div
      data-testid="leaderboard-page"
      className="mx-auto w-full max-w-[1000px] px-5 py-8 md:px-gutter md:py-10"
    >
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Leaderboard" },
        ]}
      />
      <div className="mt-6 flex items-center gap-3">
        <Trophy size={26} className="text-indigo-text" />
        <div>
          <div className="text-eyebrow uppercase text-ink-muted">Community</div>
          <h1 className="text-title-page text-ink-text">Leaderboard</h1>
        </div>
      </div>
      <p className="mt-2 max-w-[64ch] text-body text-ink-muted">
        Collectors ranked by upvotes received on approved showcases.
        Tap a name to see their public gallery.
      </p>

      {state === "loading" && (
        <div data-testid="leaderboard-loading" className="mt-12 text-body text-ink-muted">
          Loading…
        </div>
      )}
      {state === "error" && (
        <div data-testid="leaderboard-error" className="mt-12 text-body text-ink-muted">
          {error}
        </div>
      )}
      {state === "ready" && data && (
        <>
          {data.results.length === 0 ? (
            <div data-testid="leaderboard-empty" className="mt-12 rounded-card border border-ink-chip bg-ink-surface p-8 text-body text-ink-muted">
              No approved showcases yet — as soon as the first one is
              approved, that collector goes to the top of this board.
            </div>
          ) : (
            <ul
              data-testid="leaderboard-list"
              className="mt-8 divide-y divide-ink-chip overflow-hidden rounded-card border border-ink-chip bg-ink-surface"
            >
              {data.results.map((row) => (
                <li key={row.id}>
                  <Link
                    to={`/users/${row.id}`}
                    data-testid={`leaderboard-row-${row.rank}`}
                    className="flex items-center gap-4 px-5 py-4 transition-colors duration-cc ease-cc-ease hover:bg-ink-raised"
                  >
                    <div className="w-8 text-title-section text-ink-muted tabular" data-testid={`leaderboard-rank-${row.rank}`}>
                      {row.rank}
                    </div>
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-ink-chip bg-ink-raised">
                      {row.avatar_url ? (
                        <img src={row.avatar_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-body text-ink-muted">
                          {(row.display_name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-body text-ink-text">{row.display_name}</div>
                      <div className="text-meta text-ink-muted tabular">
                        {row.approved_count} approved {row.approved_count === 1 ? "showcase" : "showcases"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-title-section text-ink-text tabular" data-testid={`leaderboard-upvotes-${row.rank}`}>
                        {row.total_upvotes}
                      </div>
                      <div className="text-eyebrow uppercase text-ink-muted">upvotes</div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <Pagination
            page={data.page}
            totalPages={data.total_pages}
            onChange={(n) => setPage(n)}
            testId="leaderboard-pagination"
          />
        </>
      )}
    </div>
  );
}
